import type { MeasurementConfig } from '@dynamika-shared/types/measurements';
import { store } from '@restore/store';
import { actions as formManagementActions } from '@restore/readerViewerSession/formManagement/formManagement.slice';
import type { ViewerToolAction } from '@restore/readerViewerSession/formManagement/formManagement.types';
import {
  calculateRanoForCurrentReading,
  type RanoCalculateRequest,
} from '@restore/scoringFormsCustomization/calculateRano';
import { calculateRecistForCurrentReading } from '@restore/scoringFormsCustomization/calculateRecist';
import { calculateFirstVisitScoringForCurrentReading } from '@restore/scoringFormsCustomization/calculateFirstVisitScoring';
import { calculatePreviousVisitScoringForCurrentReading } from '@restore/scoringFormsCustomization/calculatePreviousVisitScoring';
import { registerFormCall } from '../pendingCalls';
import { formRpcScheme } from './scheme';

type HandlerMeta = { id?: string };
type ToolAction = ViewerToolAction['payload']['action'];

/**
 * Answers "accepted", not "done": nothing behind these actions reports back.
 * Their awaitable siblings below settle on the saga's own reply instead.
 * See the `Handler` contract in rpc-core.
 */
const dispatchToolAction = async (
  action: ToolAction,
  data: unknown,
  meta?: HandlerMeta
) => {
  store.dispatch(
    formManagementActions.viewerToolAction({
      action,
      data: data as MeasurementConfig,
      causationId: meta?.id,
    })
  );

  return { accepted: true };
};

const dispatchAwaitableToolAction = (
  action: ToolAction,
  data: unknown,
  meta?: HandlerMeta
) => {
  const { callId, promise } = registerFormCall();

  store.dispatch(
    formManagementActions.viewerToolAction({
      action,
      data: data as MeasurementConfig,
      causationId: meta?.id,
      callId,
    })
  );

  return promise;
};

function handleActivate(data: unknown, meta?: HandlerMeta) {
  return dispatchAwaitableToolAction('activate', data, meta);
}

function handleDeactivate(data: unknown, meta?: HandlerMeta) {
  return dispatchAwaitableToolAction('deactivate', data, meta);
}

function handleLock(data: unknown, meta?: HandlerMeta) {
  return dispatchAwaitableToolAction('lock', data, meta);
}

function handleUnlock(data: unknown, meta?: HandlerMeta) {
  return dispatchAwaitableToolAction('unlock', data, meta);
}

function handleDelete(data: unknown, meta?: HandlerMeta) {
  return dispatchAwaitableToolAction('delete', data, meta);
}

function handleUpdate(data: unknown, meta?: HandlerMeta) {
  return dispatchAwaitableToolAction('update', data, meta);
}

function handleScreenshotNA(data: unknown, meta?: HandlerMeta) {
  return dispatchAwaitableToolAction('screenshotNA', data, meta);
}

function handleShow(data: unknown, meta?: HandlerMeta) {
  return dispatchToolAction('show', data, meta);
}

function handleHide(data: unknown, meta?: HandlerMeta) {
  return dispatchToolAction('hide', data, meta);
}

function handleScreenshot(data: unknown, meta?: HandlerMeta) {
  return dispatchToolAction('screenshot', data, meta);
}

function handleRemoveEntry(data: unknown, meta?: HandlerMeta) {
  return dispatchToolAction('remove-entry', data, meta);
}

function handleRemoveAllEntries(data: unknown, meta?: HandlerMeta) {
  return dispatchToolAction('remove-all-entries', data, meta);
}

/** Registers every request initiated by a scoring form and handled by D7. */
export const registerFormHandlers = (): void => {
  const { measurements, calculate } = formRpcScheme.handlers;

  measurements.onActivate(handleActivate);
  measurements.onDeactivate(handleDeactivate);
  measurements.onLock(handleLock);
  measurements.onUnlock(handleUnlock);
  measurements.onDelete(handleDelete);
  measurements.onUpdate(handleUpdate);
  measurements.onScreenshotNA(handleScreenshotNA);
  measurements.onShow(handleShow);
  measurements.onHide(handleHide);
  measurements.onScreenshot(handleScreenshot);
  measurements.onRemoveEntry(handleRemoveEntry);
  measurements.onRemoveAllEntries(handleRemoveAllEntries);

  // Not dispatched: no slice reads the result and nothing else reacts to it, so
  // the answer goes straight back to the form that asked.
  calculate.onRano(data => calculateRanoForCurrentReading(data as RanoCalculateRequest));

  calculate.onRecist(() => calculateRecistForCurrentReading());

  calculate.onFirstVisitScoring(() => calculateFirstVisitScoringForCurrentReading());

  calculate.onPreviousVisitScoring(() =>
    calculatePreviousVisitScoringForCurrentReading()
  );
};
